import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { createUserSlice } from './slices/userSlice'
import { createReceiptSlice } from './slices/receiptSlice'
import { createPaymentSlice } from './slices/paymentSlice'
import { createAppSlice } from './slices/appSlice'
import type { AppState } from './types'

export const useStore = create<AppState>()(
  devtools(
    persist(
      (...args) => ({
        // Combine all slices
        ...createUserSlice(...args),
        ...createReceiptSlice(...args),
        ...createPaymentSlice(...args),
        ...createAppSlice(...args),

        // Global reset action - clears all persisted state on logout
        resetAll: () => {
          // Force clear localStorage to ensure clean slate
          localStorage.removeItem('finchpay-storage')

          const [set] = args
          set(
            {
              user: {
                phone: null,
                isAuthenticated: false,
                accessToken: null,
              },
              receipt: {
                capturedImage: null,
                ocrResult: { status: 'idle' },
                editedTotal: null,
              },
              payment: {
                tipOption: 18,
                customTipAmount: 0,
                paymentResult: { status: 'idle' },
              },
              app: {
                venue: null,
                pendingVenueId: null,
                error: null,
              },
            },
            undefined,
            'resetAll'
          )
        },
      }),
      {
        name: 'finchpay-storage',
        // Only persist user session and app config (venueId), not receipt/payment flow
        partialize: (state) => ({
          user: state.user,
          app: state.app,
        }),
      }
    ),
    { name: 'FinchPay' }
  )
)

// Re-export types for convenience
export type {
  AppState,
  AsyncState,
  OCRResult,
  PaymentResult,
  TipOption,
  Venue,
  ReceiptItem,
  VenueSession,
  AllowedMethod,
} from './types'
